import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, SlidersHorizontal, Star } from "lucide-react";
import { PageShell } from "../components/Layout";
import { RoomCard } from "../components/Cards";
import { apartmentData, apartmentPills } from "../utils/image-data";
import "./Accommodation.css";

/**
 * Preloads a flat list of image URLs into the browser cache.
 * Called once on mount so switching apartment pills later is a
 * cache hit instead of a fresh network fetch.
 */
function usePreloadImages(urls) {
  useEffect(() => {
    let cancelled = false;
    urls.forEach((src) => {
      if (!src || cancelled) return;
      const img = new Image();
      img.src = src;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Tracks whether a given image URL has finished loading (or was
 * already cached, e.g. by usePreloadImages). Used to fade images
 * in instead of letting them pop in mid-paint.
 */
function useImageLoaded(src) {
  const [loaded, setLoaded] = useState(false);
  const cache = useRef(new Set());

  useEffect(() => {
    if (!src) return;

    if (cache.current.has(src)) {
      setLoaded(true);
      return;
    }

    setLoaded(false);
    const img = new Image();
    img.onload = () => {
      cache.current.add(src);
      setLoaded(true);
    };
    img.onerror = () => setLoaded(true); // fail open, don't hang the UI
    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return loaded;
}

function FadeBackground({ src, className, style, children }) {
  const loaded = useImageLoaded(src);
  return (
    <div
      className={className}
      style={{
        ...style,
        backgroundImage: `url(${src})`,
        opacity: loaded ? 1 : 0,
        transition: "opacity 0.35s ease",
      }}
    >
      {children}
    </div>
  );
}

function FadeImage({ src, alt, priority = false, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchpriority={priority ? "high" : "auto"}
      decoding="async"
      onLoad={() => setLoaded(true)}
      style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.35s ease" }}
      {...rest}
    />
  );
}

export default function Accommodation() {
  const [selectedApartment, setSelectedApartment] = useState("all");

  const activeApartment = useMemo(() => {
    return apartmentData.find((apartment) => apartment.id === selectedApartment) || apartmentData[0];
  }, [selectedApartment]);

  const showingAllApartments = selectedApartment === "all";

  // Preload every apartment's images once, up front, so pill clicks
  // never hit the network cold.
  const allImageUrls = useMemo(
    () => apartmentData.flatMap((apartment) => apartment.images),
    []
  );
  usePreloadImages(allImageUrls);

  return (
    <PageShell>
      <main className="accommodation container">
        <section className="accommodation__hero">
          <div>
            <p className="eyebrow">The Bryan Residence · Lekki Phase 1</p>
            <h1>Choose your<br /><em>three-bedroom stay.</em></h1>
            <p>Discover Study, Story, Silver Screen, Odeon and Disco Flame Apartments — luxury automated holiday apartments designed for extraordinary stays in Lagos.</p>
            <div className="accommodation__hero-actions">
              <a href="#stays">Explore apartments <ArrowRight size={16} /></a>
              <span>From ₦300,000 per night</span>
            </div>
          </div>

          <FadeBackground
            src={activeApartment.images[0]}
            className="accommodation__hero-image"
          >
            <span>Lekki Phase 1, Lagos</span>
            <article>
              <b>From ₦300,000<small>/ night</small></b>
              <p>{activeApartment.name}</p>
              <a href="#stays">View apartment <ArrowUpRight size={14} /></a>
            </article>
          </FadeBackground>
        </section>

        <section className="accommodation__browse" id="stays">
          <div className="accommodation__browse-head">
            <div>
              <p className="eyebrow">A residence for every stay</p>
              <h2>Choose your apartment.</h2>
            </div>
          </div>

          <div className="accommodation__filters" aria-label="Choose an apartment">
            {apartmentPills.map((pill) => (
              <button
                type="button"
                key={pill.id}
                onClick={() => setSelectedApartment(pill.id)}
                className={selectedApartment === pill.id ? "accommodation__filter--active" : ""}
              >
                {pill.label}
              </button>
            ))}
          </div>

          {showingAllApartments ? (
            <div className="accommodation__grid">
              {
                apartmentData.map((apartment) => {
                  return (
                    <RoomCard
                      key={apartment.id}
                      name={apartment.name}
                      details={apartment.details}
                      price={apartment.price}
                      rating={apartment.rating}
                      image={apartment.images[0]}
                    />
                  )
                })
              }
            </div>
          ) : (
            <section className="accommodation__gallery">
              <div className="accommodation__gallery-heading">
                <div>
                  <span>★ {activeApartment.rating}</span>
                  <h3>{activeApartment.name}</h3>
                  <p>{activeApartment.details}</p>
                </div>
                <div>
                  <b>{activeApartment.price}</b>
                  <small>{activeApartment.images.length} apartment images</small>
                </div>
              </div>

              <div className="accommodation__gallery-grid">
                {activeApartment.images.map((image, index) => (
                  <article className="accommodation__gallery-image" key={`${activeApartment.id}-${index}`}>
                    <FadeImage
                      src={image}
                      alt={`${activeApartment.name} — view ${index + 1}`}
                      priority={index === 0}
                    />
                    <span>{String(index + 1).padStart(2, "0")}</span>
                  </article>
                ))}
              </div>
            </section>
          )}
        </section>

        <section className="accommodation__feature">
          <div className="accommodation__feature-copy">
            <p className="eyebrow">The Bryan Residence standard</p>
            <h2>Luxury,<br /><em>intelligently lived.</em></h2>
            <p>Every apartment combines refined interiors, smart automation, three bedrooms and the comfort of a fully supported stay.</p>
            <a href="/contact">Make an enquiry <ArrowUpRight size={15} /></a>
          </div>
          <FadeBackground
            src={activeApartment.images[1] || activeApartment.images[0]}
            className="accommodation__feature-image"
          />
          <div className="accommodation__feature-detail">
            <span><Star size={11} fill="currentColor" /> {activeApartment.rating} guest rating</span>
            <h3>{activeApartment.name}</h3>
            <p>{activeApartment.details}</p>
            <b>From ₦300,000<small>/ night</small></b>
          </div>
        </section>

        <section className="accommodation__count">
          <b>Five<small>+</small></b>
          <p>Distinct luxury automated apartment experiences at The Bryan Residence.</p>
        </section>

        <FadeBackground
          src={activeApartment.images[2] || activeApartment.images[0]}
          className="accommodation__deal"
        >
          <div className="accommodation__deal-overlay" />
          <div className="accommodation__deal-copy">
            <p className="eyebrow">Stay with certainty</p>
            <h2>Your apartment,<br/><em>thoughtfully reserved.</em></h2>
            <p>Choose your preferred residence and our team will confirm availability, your rate and every detail before your stay.</p>
          </div>
          <article className="accommodation__deal-card">
            <div className="accommodation__deal-card-top"><span>Reservation note</span><span>★ 4.95</span></div>
            <h3>{activeApartment.name}</h3>
            <p>Starting from <b>₦300,000</b> per night</p>
            <div className="accommodation__caution"><span>Refundable caution fee</span><b>₦100,000</b><small>Subject to residence terms and conditions.</small></div>
            <a href="/contact">Enquire on WhatsApp <ArrowRight size={14} /></a>
          </article>
        </FadeBackground>
      </main>
    </PageShell>
  );
}




// import React, { useMemo, useState } from "react";
// import { ArrowRight, ArrowUpRight, SlidersHorizontal, Star } from "lucide-react";
// import { PageShell } from "../components/Layout";
// import { RoomCard } from "../components/Cards";
// import { apartmentData, apartmentPills } from "../utils/image-data";
// import "./Accommodation.css";

// export default function Accommodation() {
//   const [selectedApartment, setSelectedApartment] = useState("all");

//   const activeApartment = useMemo(() => {
//     return apartmentData.find((apartment) => apartment.id === selectedApartment) || apartmentData[0];
//   }, [selectedApartment]);

//   const showingAllApartments = selectedApartment === "all";


//   return (
//     <PageShell>
//       <main className="accommodation container">
//         <section className="accommodation__hero">
//           <div>
//             <p className="eyebrow">The Bryan Residence · Lekki Phase 1</p>
//             <h1>Choose your<br /><em>three-bedroom stay.</em></h1>
//             <p>Discover Study, Story, Silver Screen, Odeon and Disco Flame Apartments — luxury automated holiday apartments designed for extraordinary stays in Lagos.</p>
//             <div className="accommodation__hero-actions">
//               <a href="#stays">Explore apartments <ArrowRight size={16} /></a>
//               <span>From ₦300,000 per night</span>
//             </div>
//           </div>

//           <div className="accommodation__hero-image" style={{ backgroundImage: `url(${activeApartment.images[0]})` }}>
//             <span>Lekki Phase 1, Lagos</span>
//             <article>
//               <b>From ₦300,000<small>/ night</small></b>
//               <p>{activeApartment.name}</p>
//               <a href="#stays">View apartment <ArrowUpRight size={14} /></a>
//             </article>
//           </div>
//         </section>

//         <section className="accommodation__browse" id="stays">
//           <div className="accommodation__browse-head">
//             <div>
//               <p className="eyebrow">A residence for every stay</p>
//               <h2>Choose your apartment.</h2>
//             </div>
//           </div>

//           <div className="accommodation__filters" aria-label="Choose an apartment">
//             {apartmentPills.map((pill) => (
//               <button
//                 type="button"
//                 key={pill.id}
//                 onClick={() => setSelectedApartment(pill.id)}
//                 className={selectedApartment === pill.id ? "accommodation__filter--active" : ""}
//               >
//                 {pill.label}
//               </button>
//             ))}
//           </div>

//           {showingAllApartments ? (
//             <div className="accommodation__grid">
//               {
//                 apartmentData.map((apartment) => {
//                   return (
//                     <RoomCard
//                       key={apartment.id}
//                       name={apartment.name}
//                       details={apartment.details}
//                       price={apartment.price}
//                       rating={apartment.rating}
//                       image={apartment.images[0]}
//                     />
//                   )
//                 })
//               }
//             </div>
//           ) : (
//             <section className="accommodation__gallery">
//               <div className="accommodation__gallery-heading">
//                 <div>
//                   <span>★ {activeApartment.rating}</span>
//                   <h3>{activeApartment.name}</h3>
//                   <p>{activeApartment.details}</p>
//                 </div>
//                 <div>
//                   <b>{activeApartment.price}</b>
//                   <small>{activeApartment.images.length} apartment images</small>
//                 </div>
//               </div>

//               <div className="accommodation__gallery-grid">
//                 {activeApartment.images.map((image, index) => (
//                   <article className="accommodation__gallery-image" key={`${activeApartment.id}-${index}`}>
//                     <img src={image} alt={`${activeApartment.name} — view ${index + 1}`} />
//                     <span>{String(index + 1).padStart(2, "0")}</span>
//                   </article>
//                 ))}
//               </div>
//             </section>
//           )}
//         </section>

//         <section className="accommodation__feature">
//           <div className="accommodation__feature-copy">
//             <p className="eyebrow">The Bryan Residence standard</p>
//             <h2>Luxury,<br /><em>intelligently lived.</em></h2>
//             <p>Every apartment combines refined interiors, smart automation, three bedrooms and the comfort of a fully supported stay.</p>
//             <a href="/contact">Make an enquiry <ArrowUpRight size={15} /></a>
//           </div>
//           <div className="accommodation__feature-image" style={{ backgroundImage: `url(${activeApartment.images[1] || activeApartment.images[0]})` }} />
//           <div className="accommodation__feature-detail">
//             <span><Star size={11} fill="currentColor" /> {activeApartment.rating} guest rating</span>
//             <h3>{activeApartment.name}</h3>
//             <p>{activeApartment.details}</p>
//             <b>From ₦300,000<small>/ night</small></b>
//           </div>
//         </section>

//         <section className="accommodation__count">
//           <b>Five<small>+</small></b>
//           <p>Distinct luxury automated apartment experiences at The Bryan Residence.</p>
//         </section>

//         <section className="accommodation__deal" style={{ backgroundImage: `url(${activeApartment.images[2] || activeApartment.images[0]})` }}>
//           <div className="accommodation__deal-overlay" />
//           <div className="accommodation__deal-copy">
//             <p className="eyebrow">Stay with certainty</p>
//             <h2>Your apartment,<br/><em>thoughtfully reserved.</em></h2>
//             <p>Choose your preferred residence and our team will confirm availability, your rate and every detail before your stay.</p>
//           </div>
//           <article className="accommodation__deal-card">
//             <div className="accommodation__deal-card-top"><span>Reservation note</span><span>★ 4.95</span></div>
//             <h3>{activeApartment.name}</h3>
//             <p>Starting from <b>₦300,000</b> per night</p>
//             <div className="accommodation__caution"><span>Refundable caution fee</span><b>₦100,000</b><small>Subject to residence terms and conditions.</small></div>
//             <a href="/contact">Enquire on WhatsApp <ArrowRight size={14} /></a>
//           </article>
//         </section>
//       </main>
//     </PageShell>
//   );
// }

